import type { AppLanguage } from './index';

type I18nBlock = Partial<
  Record<AppLanguage, { question?: string; answer?: string; title?: string; content?: string; excerpt?: string }>
>;

export type EquipeLocaleFields = { nome?: string; cargo?: string; descricao?: string };

export type EquipeI18nBlock = Partial<Record<AppLanguage, EquipeLocaleFields>>;

function langKey(lang: string): AppLanguage {
  const code = lang.split('-')[0];
  if (code === 'en' || code === 'es') return code;
  return 'pt';
}

export function getFaqQuestion(item: { question: string; i18n?: I18nBlock }, lang: string): string {
  const l = langKey(lang);
  return item.i18n?.[l]?.question?.trim() || item.i18n?.pt?.question?.trim() || item.question;
}

export function getFaqAnswer(item: { answer: string; i18n?: I18nBlock }, lang: string): string {
  const l = langKey(lang);
  return item.i18n?.[l]?.answer?.trim() || item.i18n?.pt?.answer?.trim() || item.answer;
}

export function getPostTitle(
  post: { title: string; i18n?: I18nBlock },
  lang: string
): string {
  const l = langKey(lang);
  return post.i18n?.[l]?.title?.trim() || post.i18n?.pt?.title?.trim() || post.title;
}

export function getPostExcerpt(
  post: { excerpt?: string; i18n?: I18nBlock },
  lang: string
): string {
  const l = langKey(lang);
  return post.i18n?.[l]?.excerpt?.trim() || post.i18n?.pt?.excerpt?.trim() || post.excerpt || '';
}

export function getPostContent(
  post: { content: string; i18n?: I18nBlock },
  lang: string
): string {
  const l = langKey(lang);
  return post.i18n?.[l]?.content?.trim() || post.i18n?.pt?.content?.trim() || post.content;
}

export const emptyI18nTriple = () => ({
  pt: {},
  en: {},
  es: {}
});

export const emptyEquipeI18n = (): EquipeI18nBlock => ({
  pt: { nome: '', cargo: '', descricao: '' },
  en: { nome: '', cargo: '', descricao: '' },
  es: { nome: '', cargo: '', descricao: '' }
});

export function getEquipeNome(
  member: { nome: string; i18n?: EquipeI18nBlock },
  lang: string
): string {
  const l = langKey(lang);
  return member.i18n?.[l]?.nome?.trim() || member.i18n?.pt?.nome?.trim() || member.nome;
}

export function getEquipeCargo(
  member: { cargo: string; i18n?: EquipeI18nBlock },
  lang: string
): string {
  const l = langKey(lang);
  return member.i18n?.[l]?.cargo?.trim() || member.i18n?.pt?.cargo?.trim() || member.cargo;
}

export function getEquipeDescricao(
  member: { descricao?: string; i18n?: EquipeI18nBlock },
  lang: string
): string {
  const l = langKey(lang);
  return (
    member.i18n?.[l]?.descricao?.trim() ||
    member.i18n?.pt?.descricao?.trim() ||
    member.descricao ||
    ''
  );
}

export type LandingSectionLocale = {
  title?: string;
  title2?: string;
  subtitle?: string;
  paragraph1?: string;
  paragraph2?: string;
  visionTitle?: string;
  visionText?: string;
  ctaPrimaryLabel?: string;
  ctaSecondaryLabel?: string;
};

export type LandingSectionItemLocale = {
  title?: string;
  description?: string;
  label?: string;
  duration?: string;
  features?: string[];
  popularVisas?: string[];
  requirements?: string[];
};

export type LandingSectionItem = {
  _id?: string;
  icon?: string;
  flag?: string;
  number?: number | null;
  numberSuffix?: string;
  order?: number;
  ativo?: boolean;
  i18n: { pt: LandingSectionItemLocale; en: LandingSectionItemLocale; es: LandingSectionItemLocale };
};

export type LandingSectionWidget = {
  mode: 'none' | 'posts' | 'video';
  videoUrl?: string;
  videoFile?: string;
};

export type LandingSection = {
  _id?: string;
  sectionKey: string;
  page: 'home' | 'about' | 'immigration' | 'contact';
  layout: 'simple' | 'list';
  i18n: { pt: LandingSectionLocale; en: LandingSectionLocale; es: LandingSectionLocale };
  items: LandingSectionItem[];
  widget?: LandingSectionWidget;
};

export type ResolvedSectionItem = {
  id: string;
  icon?: string;
  flag?: string;
  number?: number | null;
  numberSuffix?: string;
  title: string;
  description: string;
  label: string;
  duration: string;
  features: string[];
  popularVisas: string[];
  requirements: string[];
};

export type ResolvedSectionContent = {
  title: string;
  title2: string;
  subtitle: string;
  paragraph1: string;
  paragraph2: string;
  visionTitle: string;
  visionText: string;
  ctaPrimaryLabel: string;
  ctaSecondaryLabel: string;
  items: ResolvedSectionItem[];
};

function interpolateSiteName(value: string, siteName?: string): string {
  if (!siteName || !value.includes('{{siteName}}')) return value;
  return value.split('{{siteName}}').join(siteName);
}

function resolveSectionItem(item: LandingSectionItem, lang: string, index: number): ResolvedSectionItem {
  const l = langKey(lang);
  const block = item.i18n?.[l] || {};
  const ptBlock = item.i18n?.pt || {};
  return {
    id: item._id || String(index),
    icon: item.icon,
    flag: item.flag,
    number: item.number,
    numberSuffix: item.numberSuffix,
    title: (block.title?.trim() || ptBlock.title?.trim() || ''),
    description: (block.description?.trim() || ptBlock.description?.trim() || ''),
    label: (block.label?.trim() || ptBlock.label?.trim() || ''),
    duration: (block.duration?.trim() || ptBlock.duration?.trim() || ''),
    features: (block.features?.length ? block.features : ptBlock.features) || [],
    popularVisas: (block.popularVisas?.length ? block.popularVisas : ptBlock.popularVisas) || [],
    requirements: (block.requirements?.length ? block.requirements : ptBlock.requirements) || []
  };
}

export function resolveSectionContent(
  data: LandingSection | undefined,
  lang: string,
  fallback: ResolvedSectionContent,
  siteName?: string
): ResolvedSectionContent {
  if (!data) return fallback;

  const l = langKey(lang);
  const block = data.i18n?.[l] || {};
  const ptBlock = data.i18n?.pt || {};

  const field = (key: keyof LandingSectionLocale, fallbackValue: string): string => {
    const resolved = block[key]?.trim() || ptBlock[key]?.trim() || fallbackValue;
    return interpolateSiteName(resolved, siteName);
  };

  const items = data.items && data.items.length > 0
    ? data.items.map((item, index) => resolveSectionItem(item, lang, index))
    : fallback.items;

  return {
    title: field('title', fallback.title),
    title2: field('title2', fallback.title2),
    subtitle: field('subtitle', fallback.subtitle),
    paragraph1: field('paragraph1', fallback.paragraph1),
    paragraph2: field('paragraph2', fallback.paragraph2),
    visionTitle: field('visionTitle', fallback.visionTitle),
    visionText: field('visionText', fallback.visionText),
    ctaPrimaryLabel: field('ctaPrimaryLabel', fallback.ctaPrimaryLabel),
    ctaSecondaryLabel: field('ctaSecondaryLabel', fallback.ctaSecondaryLabel),
    items
  };
}

const BLOG_CATEGORY_KEYS: Record<string, string> = {
  imigração: 'blogCategories.immigration',
  documentos: 'blogCategories.documents',
  vistos: 'blogCategories.visas',
  cidadania: 'blogCategories.citizenship',
  trabalho: 'blogCategories.work',
  estudos: 'blogCategories.studies',
  geral: 'blogCategories.general'
};

export function getBlogCategoryLabelKey(category: string): string {
  return BLOG_CATEGORY_KEYS[category] || BLOG_CATEGORY_KEYS.geral;
}

export const BLOG_CATEGORY_VALUES = Object.keys(BLOG_CATEGORY_KEYS);

// --- Landing pages / Avatar (funil de vendas) ---

export type LandingPageLocale = {
  headline?: string;
  subheadline?: string;
  bodyHtml?: string;
  ctaLabel?: string;
  metaTitle?: string;
  metaDescription?: string;
  urgencyText?: string;
  ctaMicrocopy?: string;
};

export type LandingPageI18n = Partial<Record<AppLanguage, LandingPageLocale>>;

function resolveLandingField(i18n: LandingPageI18n | undefined, lang: string, field: keyof LandingPageLocale, fallback = ''): string {
  const l = langKey(lang);
  return i18n?.[l]?.[field]?.trim() || i18n?.pt?.[field]?.trim() || fallback;
}

export function getLandingPageHeadline(lp: { headline?: string; i18n?: LandingPageI18n }, lang: string): string {
  return resolveLandingField(lp.i18n, lang, 'headline', lp.headline || '');
}

export function getLandingPageSubheadline(lp: { subheadline?: string; i18n?: LandingPageI18n }, lang: string): string {
  return resolveLandingField(lp.i18n, lang, 'subheadline', lp.subheadline || '');
}

export function getLandingPageBodyHtml(lp: { bodyHtml?: string; i18n?: LandingPageI18n }, lang: string): string {
  return resolveLandingField(lp.i18n, lang, 'bodyHtml', lp.bodyHtml || '');
}

export function getLandingPageCtaLabel(lp: { ctaLabel?: string; i18n?: LandingPageI18n }, lang: string): string {
  return resolveLandingField(lp.i18n, lang, 'ctaLabel', lp.ctaLabel || '');
}

export function getLandingPageMetaTitle(lp: { metaTitle?: string; i18n?: LandingPageI18n }, lang: string): string {
  return resolveLandingField(lp.i18n, lang, 'metaTitle', lp.metaTitle || '');
}

export function getLandingPageMetaDescription(lp: { metaDescription?: string; i18n?: LandingPageI18n }, lang: string): string {
  return resolveLandingField(lp.i18n, lang, 'metaDescription', lp.metaDescription || '');
}

export function getLandingPageUrgencyText(lp: { urgencyText?: string; i18n?: LandingPageI18n }, lang: string): string {
  return resolveLandingField(lp.i18n, lang, 'urgencyText', lp.urgencyText || '');
}

export function getLandingPageCtaMicrocopy(lp: { ctaMicrocopy?: string; i18n?: LandingPageI18n }, lang: string): string {
  return resolveLandingField(lp.i18n, lang, 'ctaMicrocopy', lp.ctaMicrocopy || '');
}

export type BenefitI18n = Partial<Record<AppLanguage, { text?: string }>>;

export function getBenefitText(benefit: { i18n?: BenefitI18n }, lang: string): string {
  const l = langKey(lang);
  return benefit.i18n?.[l]?.text?.trim() || benefit.i18n?.pt?.text?.trim() || '';
}

export type SocialProofI18n = Partial<Record<AppLanguage, { label?: string }>>;

export function getSocialProofLabel(item: { i18n?: SocialProofI18n }, lang: string): string {
  const l = langKey(lang);
  return item.i18n?.[l]?.label?.trim() || item.i18n?.pt?.label?.trim() || '';
}

export type AvatarLocale = {
  displayName?: string;
  headline?: string;
  bodyHtml?: string;
  ctaLabel?: string;
};

export type AvatarI18n = Partial<Record<AppLanguage, AvatarLocale>>;

function resolveAvatarField(i18n: AvatarI18n | undefined, lang: string, field: keyof AvatarLocale, fallback = ''): string {
  const l = langKey(lang);
  return i18n?.[l]?.[field]?.trim() || i18n?.pt?.[field]?.trim() || fallback;
}

export function getAvatarDisplayName(avatar: { displayName?: string; i18n?: AvatarI18n }, lang: string): string {
  return resolveAvatarField(avatar.i18n, lang, 'displayName', avatar.displayName || '');
}

export function getAvatarHeadline(avatar: { headline?: string; i18n?: AvatarI18n }, lang: string): string {
  return resolveAvatarField(avatar.i18n, lang, 'headline', avatar.headline || '');
}

export function getAvatarBodyHtml(avatar: { bodyHtml?: string; i18n?: AvatarI18n }, lang: string): string {
  return resolveAvatarField(avatar.i18n, lang, 'bodyHtml', avatar.bodyHtml || '');
}

export function getAvatarCtaLabel(avatar: { ctaLabel?: string; i18n?: AvatarI18n }, lang: string): string {
  return resolveAvatarField(avatar.i18n, lang, 'ctaLabel', avatar.ctaLabel || '');
}
